import { TableRow, TableCell, TextField, IconButton, Button } from "@material-ui/core";
import React from "react";
import { createStyles, Theme, withStyles, WithStyles } from '@material-ui/core/styles';
import ActivateLicenseDialog, { SystemActivationData } from "./ActivateLicense";
import { IdSet, LinesView, SystemCallbackType } from "./Paperbase";
import ReturnLicenseDialog from "./ReturnLicenseDialog";
import { LinesData } from "./DealsContext";
import HistoryDialog, { RawActivationData } from "./HistoryDialog";
import { HostType, LicenseType } from "./ActivateLicenseData";
import EditIcon from '@material-ui/icons/Edit';
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';
import CancelOutlinedIcon from '@material-ui/icons/CancelOutlined';
import { DeltaXMLColors, MyDeltaColors } from "./styling/DeltaColors";
import ExtendLicenseDialog from './ExtendLicenseDialog'


const styles = (theme: Theme) =>
  createStyles({
    root: {
      '& > *': {
        borderBottom: 'unset',
      },
    },
    textContainer: {
      display: 'block',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      width: '300px',
    },
    activeButton: {
      color: DeltaXMLColors.OXFORD_BLUE
    },
    inactiveButton: {
      color: MyDeltaColors.INACTIVE_FOREGROUND,
      backgroundColor:  DeltaXMLColors.CONCRETE,
      borderColor:  MyDeltaColors.INACTIVE_BORDER,
      '&:hover': {
        backgroundColor: DeltaXMLColors.CONCRETE,
        color: MyDeltaColors.INACTIVE_FOREGROUND,
        borderColor: MyDeltaColors.INACTIVE_BORDER
    }
    },
    systemData: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center'
    },
    systemTextField: {
      display: 'flex',
      flexDirection: 'row',
      width: '360px'
    },
    editableTextField: {
      width: '280px',
      paddingRight: '10px'
    },
    button: {
      margin: '2px'
    },
  });

export interface RawSystemsData {
  "fulfillmentSize": number,
  "systemId": string,
  "current_return_count": number,
  "return_count_limit": number,
  "host_id"?: string,
  "host_id2"?: string,
  "host_id3"?: string,
  "last_modified"?: string
  "user_annotation"?: string,
  "parentDeal"?: string,
  "parentLine"?: number
}

export interface SystemsData {
  "fulfillmentSize": number,
  "systemId": string,
  "returnCountLimit": number,
  "returnCount"?: number,
  "hostId"?: string,
  "hostId2"?: string,
  "hostId3"?: string,
  "hostIdType"?: string,
  "hostId2Type"?: string,
  "hostId3Type"?: string,
  "lastModified"?: string
  "rawActivations": RawActivationData[],
  "user_annotation"?: string
}

export interface SystemsContextProps extends WithStyles<typeof styles> {
  dealId: string,
  line: LinesData,
  systemData: SystemsData;
  hasExpired: boolean;
  systemButtons: SystemButtonState;
  callSystemAction: SystemCallbackType;
  downloadLicense: (dealId:string, lineNumber:string, systemId: string, licenseModel: string) => void;
  addUserAnnotationToSystem: (idSet: IdSet, user_annotation: string) => void;
  linesView: LinesView; 
  enableSystemActionButtons: boolean;
  dateFormat: string;
  timeFormat: string;
}

export type LineSystemCollection = { [name: string]: SystemCollection };
export type SystemCollection = { [name: string]: SystemsData };

export enum SystemButton {
  activate = 'activate',
  download = 'download',
  history = 'history',
  return = 'return',
  createSystem = 'createSystem'
}

 export class SystemButtonState {
   activate = true;
   download = true;
   history = true;
   return = true;
   createSystem = true;
 }

 export type SystemButtonStateCollection = { [name: string]: SystemButtonState }

 

function SystemsContext(props: SystemsContextProps) {
  const { dealId, linesView, line, systemData, classes, hasExpired, systemButtons, callSystemAction, downloadLicense, addUserAnnotationToSystem, enableSystemActionButtons, dateFormat, timeFormat } = props;
  const emptyHostIdString = '---'
  const hostIdText = systemData.hostId ? systemData.hostId : emptyHostIdString;
  const hostId2Text = systemData.hostId2 ? systemData.hostId2 : emptyHostIdString;
  const hostId3Text = systemData.hostId3 ? systemData.hostId3 : emptyHostIdString;
  const hostIdType = (systemData.hostIdType && systemData.hostIdType !== HostType.na) ? systemData.hostIdType : emptyHostIdString;
  const hostId2Type = (systemData.hostId2Type && systemData.hostId2Type !== HostType.na) ? systemData.hostId2Type : emptyHostIdString;
  const hostId3Type = (systemData.hostId3Type && systemData.hostId3Type !== HostType.na) ? systemData.hostId3Type : emptyHostIdString;
  const triadSystem = systemData.hostId && systemData.hostId && systemData.hostId3;

  const [open, setOpen] = React.useState(false);
  const [openDeactivate, setOpenDeactivate] = React.useState(false);
  const [historyOpen, setHistoryOpen] = React.useState(false);
  const [extendLicenseOpen, setExtendLicenseOpen] = React.useState(false);

  const evalTrialLicense = line.licenseModel === LicenseType.trial || line.licenseModel === LicenseType.trialCO;
  const notRedist = line.licenseModel !== LicenseType.redistribution;
  const noHostidRequired = line.licenseModel === LicenseType.server_boeing ||
                          line.licenseModel === LicenseType.server_unlocked ||
                          line.licenseModel === LicenseType.server_worldbox;
  const isFetchingHistory = systemButtons && !systemButtons[SystemButton.history];

  const handleExtendLicenseOpen = () => {
    setExtendLicenseOpen(true);
  }

  const handleExtendLicenseClose = () => {
    setExtendLicenseOpen(false);
  }

  const handleHistoryOpen = () => {
    setHistoryOpen(true);
    callSystemAction([null, null, systemData.systemId], SystemButton.history);
  };

  const handleHistoryClose = () => {
    setHistoryOpen(false);
  }

  const handleClickOpen = () => {
    let activateButtonActive = true;
    if (systemButtons !== undefined) {
      activateButtonActive = systemButtons[SystemButton.activate];
    }
    if (activateButtonActive) {
      setOpen(true);
    }
  };
  
  const handleClose = () => {
    setOpen(false);
  };

  const handleDeactivateClickOpen = () => {
    let deactivateButtonActive = true;
    if (systemButtons !== undefined) {
      deactivateButtonActive = systemButtons[SystemButton.return];
    }
    if (deactivateButtonActive) {
      setOpenDeactivate(true);
      callSystemAction([null, null, systemData.systemId], SystemButton.history);
    }
  };

  const handleDeactivateConfirm = () => {
    setOpenDeactivate(false);

    callSystemAction([null, null, systemData.systemId], SystemButton.return);
  }
  
  const handleActivation = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>, data: SystemActivationData) => {
    callSystemAction([null, null, systemData.systemId], SystemButton.activate, data);
    handleClose();
  }

  const download = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    downloadLicense(dealId, line.lineNumber.toString(), systemData.systemId, line.licenseModel);
  }

  const hasHostId = (!!systemData.hostId) || (systemData.hostId === '' && noHostidRequired);
  const linkClass = (button: SystemButton) => {
    let buttonActive = true;
    if (systemButtons !== undefined) {
      buttonActive = systemButtons[button];
    }
    const activeClass = buttonActive && enableSystemActionButtons? classes.activeButton : classes.inactiveButton;
    return classes.button + ' ' + activeClass;
  };

  const partialFulfillmentModel = line.allowedHostIds.includes("IP Address x3") || line.allowedHostIds.includes("Mac Address x3") || line.allowedHostIds.includes("Hostname x3");

  const [systemEditable, setSystemEditable] = React.useState(false)

  const setSystemEditableVariable = () => {
    setSystemEditable(true);
  }

  const setSystemNotEditable = () => {
    setSystemEditable(false);
  }

  const handleConfirmSystemUserAnnotation = () => {
    addUserAnnotationToSystem([dealId, line.lineNumber.toString(), systemData.systemId], userAnnotation)
    setSystemEditable(false);
  }

  const [userAnnotation, setUserAnnotation] = React.useState('')

  const handleUserAnnotationChange = (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    setUserAnnotation(event.target.value);
  }

  const rowSpan: number = triadSystem ? 3: 1;

  return (
    <React.Fragment>
    <TableRow className={classes.root}>
      <TableCell rowSpan={rowSpan} colSpan={notRedist ? 1 : 3}>
        {!systemEditable && (
          <div className={classes.systemData}>
            <div className={classes.textContainer}>{(systemData.user_annotation) ? systemData.user_annotation : systemData.systemId}</div>
            <IconButton onClick={setSystemEditableVariable}>
              <EditIcon fontSize="small"/>
            </IconButton>
          </div>
        )}

        {systemEditable && (
          <div className={classes.systemTextField}>
            <TextField hidden={systemEditable} className={classes.editableTextField}
                      defaultValue={(systemData.user_annotation) ? systemData.user_annotation : systemData.systemId} onChange={handleUserAnnotationChange}/>
            <IconButton size="small" hidden={systemEditable} onClick={handleConfirmSystemUserAnnotation}>
              <CheckCircleOutlineIcon fontSize="small" style={{ color: DeltaXMLColors.CELERY }}/>
            </IconButton>
            <IconButton size="small" hidden={systemEditable} onClick={setSystemNotEditable}>
              <CancelOutlinedIcon fontSize="small" style={{ color: MyDeltaColors.ERROR_RED }}/>
            </IconButton>
          </div>
        )}

      </TableCell>
      {notRedist && (
        <>
          <TableCell>{hostIdType}</TableCell>
          <TableCell><div className={classes.textContainer}>{hostIdText}</div></TableCell>
        </>
      )}
      {partialFulfillmentModel && (
        <TableCell rowSpan={rowSpan}>{systemData.fulfillmentSize}</TableCell>
      )}
      <TableCell rowSpan={rowSpan}>
        {!hasExpired && (
          <>
            {!hasHostId && (linesView === LinesView.CURRENT || linesView ===  LinesView.CURRENT_EVALS) && notRedist && (
              <React.Fragment>
                <Button variant="contained" color="primary" size="small" className={linkClass(SystemButton.activate)} onClick={handleClickOpen}>Activate</Button>
                <ActivateLicenseDialog dealId={dealId} line={line} dialogOpen={open} handleCloseDialog={handleClose} handleActivation={handleActivation} dateFormat={dateFormat}/> 
              </React.Fragment> 
            )}
            {(hasHostId || !notRedist) &&  (
              <>
              {notRedist && (linesView === LinesView.CURRENT || linesView === LinesView.CURRENT_EVALS) && !noHostidRequired &&  (
                <>
                <Button variant="contained" color="primary" size="small" className={linkClass(SystemButton.return)} onClick={handleDeactivateClickOpen}>Return</Button>
                <ReturnLicenseDialog dialogOpen={openDeactivate} handleDeactivateCancel={() => setOpenDeactivate(false)} handleDeactivateConfirm={handleDeactivateConfirm} systemData={systemData}/>
                </>
              )}
              {(linesView === LinesView.CURRENT || linesView === LinesView.CURRENT_EVALS) && (
                <Button variant="contained" color="primary" size="small" className={linkClass(SystemButton.download)} onClick={download}>Download</Button>
              )}
              </>
            )}
          </>
        )}
        {evalTrialLicense && (
          <>
          <Button variant="contained" color="primary" size="small" className={classes.button} onClick={handleExtendLicenseOpen}>Extend</Button>
          <ExtendLicenseDialog dialogOpen={extendLicenseOpen} handleExtendLicenseClose={handleExtendLicenseClose}/>
          </>
        )}
        {notRedist &&  (
        <>
        <Button variant="contained" color="primary" size="small" className={linkClass(SystemButton.history)} onClick={handleHistoryOpen}>History</Button>
        <HistoryDialog dialogOpen={historyOpen} fetchingHistory={isFetchingHistory} noHostIdsRequired={noHostidRequired} handleRefresh={handleHistoryOpen} handleHistoryClose={handleHistoryClose} systemData={systemData} dateFormat={dateFormat} timeFormat={timeFormat}/>
        </>
        )}
      </TableCell>
    </TableRow>
    {triadSystem && (
      <>
        <TableRow className={classes.root}>
          {notRedist && (
            <>
              <TableCell>{hostId2Type}</TableCell>
              <TableCell><div className={classes.textContainer}>{hostId2Text}</div></TableCell>
            </>
          )}
        </TableRow>
        <TableRow className={classes.root}>
          {notRedist && (
            <>
              <TableCell>{hostId3Type}</TableCell>
              <TableCell><div className={classes.textContainer}>{hostId3Text}</div></TableCell>
            </>
          )}
        </TableRow>
      </>
      )}
    </React.Fragment>
  );
}

export default withStyles(styles)(SystemsContext);